import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

// ---- Option sets (module scope so they stay stable across renders) ----
const DISCHARGE_TYPE_OPTIONS = [
  "Planned Discharge",
  "Administrative Grounds",
  "Medical Grounds",
  "Transferred to other facility (Referral form provided as per policy)",
  "LAMA Left Against Medical Advise (Counseling done, consequences explained as per LAMA policy)",
  "DOR – Discharge on Request",
];

const SERVICES_OPTIONS = [
  "Psychiatric consultation & treatment",
  "Primary-care services",
  "Psychiatric rehabilitation services",
  "Individual Therapy",
  "Group Therapy",
  "Psychoeducation for the client",
  "Psychoeducation for the family",
  "Family Counseling",
  "Recreational / Occupational Therapy",
  "Activities of Daily Living (ADL) Support",
  "Nutritional Counseling, Physical Wellness Training",
  "Weekly and monthly outings",
];

const PROGRESS_OPTIONS = ["Marked Improvement", "Moderate Progress", "No Change", "Needs Further Intervention"];

const CONDITION_OPTIONS = [
  "Stable",
  "Improved",
  "Readiness to Discharge",
  "Risk Managed",
  "Minimal Supervision Required",
  "Requires Long-Term Monitoring",
  "Insight Improved but Partial",
  "Symptoms Persist – Controlled",
  "Behavioral Support Needed",
];

const IRP_DOMAIN_OPTIONS = ["Living", "Learning", "Socializing", "Working"];

const INSTRUCTIONS_OPTIONS = [
  "Verbal instructions given",
  "Written prescription attached",
  "Continue till next psychiatric review",
  "Side effects & compliance discussed",
];

const FAMILY_COUNSELING_OPTIONS = [
  "Client psychiatric illness",
  "Maintaining hygiene",
  "Medication use side effects and monitoring /supervision for compliance including checking mouth after taking medication",
  "Follow-up after 14 days / earlier if symptoms worsen",
  "Addressing family expectations",
  "Monitor closely for any increase in symptoms",
];

const EDUCATION_OPTIONS = ["Client", "Family", "Guardian"];

// Transition For — "Day Rehab" is stored as "Day Care" to keep the existing
// replication trigger in the backend controller working. DO NOT change the value.
const TRANSITION_OPTIONS = [
  { label: "Day Rehab", value: "Day Care" },
  { label: "Home Visitation", value: "Home Visitation" },
  { label: "Vocational Activities", value: "Vocational Activities" },
  { label: "Other", value: "Other" },
];

// Hoisted to module scope so its identity is stable across renders
// (defining it inside the form would make inputs lose focus each keystroke).
const Section = ({ id, title, show, children }) => (
  <div className="accordion-item mb-3 shadow-sm rounded">
    <h2 className="accordion-header">
      <button className={`accordion-button fw-semibold ${show ? "" : "collapsed"}`} type="button" data-bs-toggle="collapse" data-bs-target={`#${id}`}>
        {title}
      </button>
    </h2>
    <div id={id} className={`accordion-collapse collapse ${show ? "show" : ""}`}>
      <div className="accordion-body pt-3">{children}</div>
    </div>
  </div>
);

const DischargeSummaryForm = ({
  formData,
  handleChange,
  handleMedicationsChange,
  program,
  isSubmitting = false,
  isEdit = false,
  fileData = {},
}) => {
  const [medications, setMedications] = useState(
    formData.discharge_medications || [{ id: Date.now(), name: "", dosage: "", frequency: "", route: "" }]
  );

  // ---- CNIC masking ----
  const formatCNIC = (value) => {
    if (!value) return value;
    const cnic = value.replace(/[^\d]/g, "");
    const len = cnic.length;
    if (len <= 5) return cnic;
    if (len <= 12) return `${cnic.slice(0, 5)}-${cnic.slice(5)}`;
    return `${cnic.slice(0, 5)}-${cnic.slice(5, 12)}-${cnic.slice(12, 13)}`;
  };
  const handleCNICChange = (e) => {
    handleChange({ target: { name: e.target.name, value: formatCNIC(e.target.value) } });
  };

  // ---- Medications sync (kept from original implementation) ----
  const initialRender = useRef(true);
  const prevMedicationsRef = useRef(medications);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    const changed = JSON.stringify(medications) !== JSON.stringify(prevMedicationsRef.current);
    if (changed && handleMedicationsChange) {
      handleMedicationsChange(medications);
      prevMedicationsRef.current = medications;
    }
  }, [medications, handleMedicationsChange]);

  useEffect(() => {
    prevMedicationsRef.current = medications;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formData?.discharge_medications?.length) {
      const normalized = formData.discharge_medications.map((m, idx) => ({
        ...m,
        id: m.id || m.tempId || Date.now() + idx,
      }));
      setMedications(normalized);
      prevMedicationsRef.current = normalized;
    }
  }, [formData.discharge_medications]);

  const addMedication = () => {
    if (medications.length >= 10) return;
    setMedications([...medications, { id: Date.now(), name: "", dosage: "", frequency: "", route: "" }]);
  };
  const removeMedication = (id) => {
    if (medications.length === 1) return;
    setMedications(medications.filter((m) => m.id !== id && m.tempId !== id));
  };
  const handleMedChange = (id, field, value) => {
    setMedications(medications.map((m) => ((m.id === id || m.tempId === id) ? { ...m, [field]: value } : m)));
  };

  // ---- Checkbox helper (inline, no inner component) ----
  const isChecked = (field, value) => Array.isArray(formData[field]) && formData[field].includes(value);

  const renderCheckGroup = (field, options, colClass = "col-md-6") =>
    options.map((opt) => {
      const value = typeof opt === "object" ? opt.value : opt;
      const label = typeof opt === "object" ? opt.label : opt;
      return (
        <div className={colClass} key={value}>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id={`${field}_${value}`}
              name={field}
              value={value}
              checked={isChecked(field, value)}
              onChange={handleChange}
            />
            <label className="form-check-label" htmlFor={`${field}_${value}`}>{label}</label>
          </div>
        </div>
      );
    });

  // ---- Signature preview src ----
  const sigPreview = (field) => {
    const fd = fileData[field];
    if (fd?.file) return URL.createObjectURL(fd.file);
    if (fd?.existingUrl) return fd.existingUrl;
    return null;
  };

  return (
    <div>
      {/* Header */}
      <div className="text-center mb-3">
        <h4 className="fw-bold mb-1">THE RECOVERY HOUSE</h4>
        <h6 className="mb-2">Discharge Summary &amp; After Care Plan</h6>
        <p className="text-muted small mb-0">
          Confidential Medical Record – This document contains sensitive health information. It is strictly confidential and
          intended only for authorized individuals including the client, their legal guardian, and clinical staff.
          Unauthorized access, sharing, or duplication is prohibited. Handle and store according to TRH confidentiality policy.
        </p>
      </div>

      <div className="accordion" id="dischargeSummaryAccordion">
        {/* 1. Client Information */}
        <Section id="dsClientInfo" title="Client Information" show>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Name</label>
              <input type="text" className="form-control" name="client_name" value={formData.client_name || ""} onChange={handleChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label">MR No. / CNIC</label>
              <input type="text" className="form-control" name="CNIC" value={formData.CNIC || ""} onChange={handleCNICChange} maxLength={15} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Date of Birth</label>
              <input type="date" className="form-control" name="date_of_birth" value={formData.date_of_birth || ""} onChange={handleChange} />
            </div>
            <div className="col-md-4">
              <label className="form-label d-block">Gender</label>
              {["Male", "Female", "Other"].map((g) => (
                <div className="form-check form-check-inline" key={g}>
                  <input className="form-check-input" type="radio" name="gender" id={`gender_${g}`} value={g} checked={(formData.gender || "").toLowerCase() === g.toLowerCase()} onChange={handleChange} />
                  <label className="form-check-label" htmlFor={`gender_${g}`}>{g}</label>
                </div>
              ))}
            </div>
            <div className="col-md-4">
              <label className="form-label">Program</label>
              <input type="text" className="form-control" name="program" value={formData.program || ""} onChange={handleChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Date of Admission</label>
              <input type="date" className="form-control" name="admission_date" value={formData.admission_date || ""} onChange={handleChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Date of Discharge</label>
              <input type="date" className="form-control" name="discharge_date" value={formData.discharge_date || ""} onChange={handleChange} />
            </div>
          </div>
        </Section>

        {/* 2. Provisional Psychiatric Diagnosis */}
        <Section id="dsDiagnosis" title="Provisional Psychiatric Diagnosis">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Primary Diagnosis</label>
              <textarea className="form-control" rows="2" name="primary_diagnosis" value={formData.primary_diagnosis || ""} onChange={handleChange}></textarea>
            </div>
            <div className="col-md-4">
              <label className="form-label">Secondary Diagnosis</label>
              <textarea className="form-control" rows="2" name="secondary_diagnosis" value={formData.secondary_diagnosis || ""} onChange={handleChange}></textarea>
            </div>
            <div className="col-md-4">
              <label className="form-label">Co-morbidities</label>
              <textarea className="form-control" rows="2" name="co_morbidities" value={formData.co_morbidities || ""} onChange={handleChange}></textarea>
            </div>
          </div>
        </Section>

        {/* 3. Discharge Planning */}
        <Section id="dsPlanning" title="Discharge Planning">
          <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Discharge Type</small>
          <div className="row g-2 mb-3">{renderCheckGroup("discharge_types", DISCHARGE_TYPE_OPTIONS, "col-md-6")}</div>

          <div className="row g-3">
            <div className="col-md-6">
              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Services Provided at TRH</small>
              <div className="row g-2">{renderCheckGroup("service_provided", SERVICES_OPTIONS, "col-12")}</div>
            </div>
            <div className="col-md-6">
              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Presenting Symptoms</small>
              <textarea className="form-control" rows="12" name="presenting_symptoms" value={formData.presenting_symptoms || ""} onChange={handleChange}></textarea>
            </div>
          </div>
        </Section>

        {/* 4. Clinical Reports (remarks + attachment per report) */}
        <Section id="dsReports" title="Clinical Reports">
          <div className="row g-3">
            {[
              { remarks: "psychiatrist_remarks", att: "psychiatrist_attachment", label: "Psychiatrist's Report" },
              { remarks: "care_manager_remarks", att: "care_manager_attachment", label: "Case Manager's Report" },
              { remarks: "primary_care_remarks", att: "primary_care_attachment", label: "Physicians Report" },
            ].map(({ remarks, att, label }) => (
              <div className="col-md-4" key={att}>
                <label className="form-label fw-semibold">{label}</label>
                <textarea className="form-control mb-2" rows="3" name={remarks} value={formData[remarks] || ""} onChange={handleChange}></textarea>
                {fileData[att]?.existingUrl && !fileData[att]?.file && (
                  <a href={fileData[att].existingUrl} target="_blank" rel="noopener noreferrer" className="d-block small mb-1">
                    <i className="ri-attachment-2 me-1"></i> View current attachment
                  </a>
                )}
                <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="form-control" name={att} onChange={handleChange} />
              </div>
            ))}
          </div>
        </Section>

        {/* 5. Progress since time of admission */}
        <Section id="dsProgress" title="Progress since time of admission">
          <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Progress</small>
          <div className="row g-2 mb-3">{renderCheckGroup("clinical_progress", PROGRESS_OPTIONS, "col-md-6")}</div>

          <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Condition at time of discharge</small>
          <div className="row g-2 mb-3">{renderCheckGroup("mental_state_at_discharge", CONDITION_OPTIONS, "col-md-6")}</div>

          <div>
            <label className="form-label">Complications (if any)</label>
            <textarea className="form-control" rows="3" name="complications" value={formData.complications || ""} onChange={handleChange}></textarea>
          </div>
        </Section>

        {/* 6. Individualized Rehabilitation Plan, Progress and Outcomes */}
        <Section id="dsIrp" title="Individualized Rehabilitation Plan, Progress and Outcomes">
          <div className="row g-2 mb-3">{renderCheckGroup("irp_domains", IRP_DOMAIN_OPTIONS, "col-md-3 col-6")}</div>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label">Short-Term Goals</label>
              <textarea className="form-control" rows="2" name="short_term_goals" value={formData.short_term_goals || ""} onChange={handleChange}></textarea>
            </div>
            <div className="col-md-6">
              <label className="form-label">Long-Term Goals</label>
              <textarea className="form-control" rows="2" name="long_term_goals" value={formData.long_term_goals || ""} onChange={handleChange}></textarea>
            </div>
          </div>

          <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Current Progress</small>
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <div className="form-check mb-1">
                <input className="form-check-input" type="checkbox" id="cp_achieved" name="current_progress" value="Achieved" checked={isChecked("current_progress", "Achieved")} onChange={handleChange} />
                <label className="form-check-label" htmlFor="cp_achieved">Achieved</label>
              </div>
              <input type="text" className="form-control" placeholder="Details" name="progress_achieved_text" value={formData.progress_achieved_text || ""} onChange={handleChange} />
            </div>
            <div className="col-md-4">
              <div className="form-check mb-1">
                <input className="form-check-input" type="checkbox" id="cp_inprogress" name="current_progress" value="In Progress" checked={isChecked("current_progress", "In Progress")} onChange={handleChange} />
                <label className="form-check-label" htmlFor="cp_inprogress">In Progress</label>
              </div>
              <input type="text" className="form-control" placeholder="Details" name="progress_in_progress_text" value={formData.progress_in_progress_text || ""} onChange={handleChange} />
            </div>
            <div className="col-md-4">
              <div className="form-check mb-1">
                <input className="form-check-input" type="checkbox" id="cp_notachieved" name="current_progress" value="Not Achieved" checked={isChecked("current_progress", "Not Achieved")} onChange={handleChange} />
                <label className="form-check-label" htmlFor="cp_notachieved">Not Achieved</label>
              </div>
              <input type="text" className="form-control" placeholder="Details" name="progress_not_achieved_text" value={formData.progress_not_achieved_text || ""} onChange={handleChange} />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Additional Responsibilities / current progress</label>
            <textarea className="form-control" rows="2" name="additional_responsibilities" value={formData.additional_responsibilities || ""} onChange={handleChange}></textarea>
          </div>
          <div>
            <label className="form-label">Outcomes</label>
            <textarea className="form-control" rows="2" name="outcomes" value={formData.outcomes || ""} onChange={handleChange}></textarea>
          </div>
        </Section>

        {/* 7. Discharge Medication */}
        <Section id="dsMedication" title="Discharge Medication">
          <div className="table-responsive">
            <table className="table table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "5%" }}>#</th>
                  <th>Medication Name</th>
                  <th style={{ width: "22%" }}>Dosage Frequency</th>
                  <th style={{ width: "26%" }}>Route</th>
                  <th style={{ width: "6%" }}></th>
                </tr>
              </thead>
              <tbody>
                {medications.map((med, index) => {
                  const rowId = med.id || med.tempId;
                  return (
                    <tr key={rowId}>
                      <td>{String(index + 1).padStart(2, "0")}</td>
                      <td><input type="text" className="form-control form-control-sm" value={med.name || ""} onChange={(e) => handleMedChange(rowId, "name", e.target.value)} /></td>
                      <td><input type="text" className="form-control form-control-sm" value={med.dosage || ""} onChange={(e) => handleMedChange(rowId, "dosage", e.target.value)} /></td>
                      <td>
                        {["Oral", "IM", "IV"].map((r) => (
                          <div className="form-check form-check-inline" key={r}>
                            <input className="form-check-input" type="radio" name={`route_${rowId}`} id={`route_${rowId}_${r}`} checked={med.route === r} onChange={() => handleMedChange(rowId, "route", r)} />
                            <label className="form-check-label" htmlFor={`route_${rowId}_${r}`}>{r}</label>
                          </div>
                        ))}
                      </td>
                      <td>
                        {medications.length > 1 && (
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeMedication(rowId)}>
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {medications.length < 10 && (
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={addMedication}>
              <i className="ri-add-line me-1"></i> Add Medication
            </button>
          )}
        </Section>

        {/* 8. Instructions */}
        <Section id="dsInstructions" title="Instructions">
          <div className="row g-2">{renderCheckGroup("instructions", INSTRUCTIONS_OPTIONS, "col-md-6")}</div>
        </Section>

        {/* 9. Family Counseling Done for */}
        <Section id="dsFamilyCounseling" title="Family Counseling Done for">
          <div className="row g-2">{renderCheckGroup("family_counseling", FAMILY_COUNSELING_OPTIONS, "col-12")}</div>
        </Section>

        {/* 10. Home Care & Future Support plan */}
        <Section id="dsHomeCare" title="Home Care & Future Support plan">
          <div className="mb-3">
            <label className="form-label d-block">Education Provided To</label>
            <div className="row g-2">{renderCheckGroup("teaching_provided_to", EDUCATION_OPTIONS, "col-md-3 col-6")}</div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label">Recovery Goals</label>
              <input type="text" className="form-control" name="recovery_goals" value={formData.recovery_goals || ""} onChange={handleChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Possible Relapse Signs</label>
              <input type="text" className="form-control" name="possible_relapse_signs" value={formData.possible_relapse_signs || ""} onChange={handleChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Injections if Any</label>
              <input type="text" className="form-control" name="injections" value={formData.injections || ""} onChange={handleChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Risk Assessment at Discharge</label>
              <input type="text" className="form-control" name="risk_assessment" value={formData.risk_assessment || ""} onChange={handleChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Who to Contact</label>
              <input type="text" className="form-control" name="who_to_contact" value={formData.who_to_contact || ""} onChange={handleChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Where to Go in Crisis</label>
              <input type="text" className="form-control" name="where_to_go_crisis" value={formData.where_to_go_crisis || ""} onChange={handleChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Follow-up Psychiatrist Appointment</label>
              <input type="date" className="form-control" name="follow_up_psychiatrist" value={formData.follow_up_psychiatrist || ""} onChange={handleChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Referred For Physical Health Needs — Other (specify)</label>
              <input type="text" className="form-control" name="physical_health_needs" value={formData.physical_health_needs || ""} onChange={handleChange} />
            </div>
            <div className="col-12">
              <label className="form-label">Special Instructions</label>
              <textarea className="form-control" rows="2" name="special_instructions" value={formData.special_instructions || ""} onChange={handleChange}></textarea>
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label d-block">Transition For</label>
            <div className="row g-2">{renderCheckGroup("referred_for", TRANSITION_OPTIONS, "col-md-3 col-6")}</div>
            {isChecked("referred_for", "Other") && (
              <input type="text" className="form-control mt-2" placeholder="Transition — Other (specify)" name="referred_for_other_text" value={formData.referred_for_other_text || ""} onChange={handleChange} />
            )}
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label d-block">Next of Kin Informed</label>
              {["Yes", "No"].map((v) => (
                <div className="form-check form-check-inline" key={v}>
                  <input className="form-check-input" type="radio" name="next_of_kin_informed" id={`nok_${v}`} value={v} checked={formData.next_of_kin_informed === v} onChange={handleChange} />
                  <label className="form-check-label" htmlFor={`nok_${v}`}>{v}</label>
                </div>
              ))}
            </div>
            <div className="col-md-6">
              <label className="form-label d-block">Emergency Contact Provided</label>
              {["Yes", "No"].map((v) => (
                <div className="form-check form-check-inline" key={v}>
                  <input className="form-check-input" type="radio" name="emergency_contact_provided" id={`ecp_${v}`} value={v} checked={formData.emergency_contact_provided === v} onChange={handleChange} />
                  <label className="form-check-label" htmlFor={`ecp_${v}`}>{v}</label>
                </div>
              ))}
            </div>
            <div className="col-12">
              <label className="form-label">Safety Care Plan (if needed)</label>
              <textarea className="form-control" rows="2" name="safety_care_plan" value={formData.safety_care_plan || ""} onChange={handleChange} placeholder="To be completed as per Risk Assessment Policy"></textarea>
            </div>
            <div className="col-12">
              <label className="form-label">Recommendations from TRH</label>
              <textarea className="form-control" rows="2" name="trh_recommendations" value={formData.trh_recommendations || ""} onChange={handleChange}></textarea>
            </div>
          </div>
        </Section>

        {/* 11. Signatures */}
        <Section id="dsSignatures" title="Signatures">
          <p className="text-muted small mb-3">Upload a signature image for each role and enter the signatory's name.</p>
          <div className="row g-3">
            {[
              { key: "consultant_psychiatrist", label: "Consultant Psychiatrist" },
              { key: "prp", label: "Psychiatric Rehabilitation Practitioner (PRP)" },
              { key: "guardian", label: "Guardian" },
              { key: "client", label: "Client" },
            ].map(({ key, label }) => {
              const nameField = key === "client" ? "client_sign_name" : `${key}_name`;
              const sigField = `${key}_signature`;
              const preview = sigPreview(sigField);
              return (
                <div className="col-md-6" key={key}>
                  <div className="border rounded-3 p-3 h-100">
                    <label className="form-label fw-semibold">{label}</label>
                    <input type="text" className="form-control mb-2" placeholder="Name" name={nameField} value={formData[nameField] || ""} onChange={handleChange} />
                    {preview && (
                      <img src={preview} alt={`${label} signature`} style={{ height: 55, maxWidth: 200, objectFit: "contain", border: "1px solid #dee2e6", borderRadius: 4, background: "#fff" }} className="d-block mb-2" />
                    )}
                    <input type="file" accept="image/*" className="form-control" name={sigField} onChange={handleChange} />
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      </div>

      {isSubmitting && <div className="text-muted small mt-2">Saving…</div>}
    </div>
  );
};

export default DischargeSummaryForm;
